import { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from "recharts";
import { useAssetChart } from "@/hooks/useAssetChart";
import { Skeleton } from "@/components/ui/skeleton";
import type { InfluencerCall } from "@/types/influencer";

const RANGES = ["Auto", "1W", "1M", "3M", "6M", "1Y", "2Y", "5Y", "10Y", "Max"] as const;

const RANGE_DAYS: Record<string, number> = {
  "1W": 7, "1M": 30, "3M": 90, "6M": 180, "1Y": 365, "2Y": 730, "5Y": 1825, "10Y": 3650,
};

function pickAutoRange(calls: InfluencerCall[]): string {
  if (!calls.length) return "1Y";
  const now = Date.now();
  const oldest = Math.min(...calls.map((c) => new Date(c.date).getTime()));
  const daysSpan = (now - oldest) / (1000 * 60 * 60 * 24);
  for (const r of ["1W", "1M", "3M", "6M", "1Y", "2Y", "5Y", "10Y"] as const) {
    if (daysSpan <= RANGE_DAYS[r]) return r;
  }
  return "Max";
}

const ACTION_COLORS: Record<string, string> = {
  BUY: "hsl(155, 100%, 50%)",
  SELL: "hsl(0, 72%, 58%)",
  TRIM: "hsl(0, 72%, 58%)",
  SHORT: "hsl(0, 72%, 58%)",
  HOLD: "hsl(48, 96%, 53%)",
  WATCH: "hsl(215, 14%, 50%)",
  NOISE: "hsl(215, 14%, 50%)",
};

interface AssetPriceChartProps {
  ticker: string;
  calls: InfluencerCall[];
  onCallClick?: (callId: string) => void;
}

function formatXLabel(dateStr: string, range: string): string {
  const d = new Date(dateStr + "T00:00:00");
  if (range === "1W" || range === "1M") {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

export default function AssetPriceChart({ ticker, calls, onCallClick }: AssetPriceChartProps) {
  const [range, setRange] = useState<string>("Auto");
  const resolvedRange = range === "Auto" ? pickAutoRange(calls) : range;
  const { data, isLoading, isError } = useAssetChart(ticker, resolvedRange === "Max" ? "MAX" : resolvedRange);

  const chartData = useMemo(() => {
    if (!data?.prices) return [];
    return data.prices.map((p, i) => ({ idx: i, date: p.date, close: p.close }));
  }, [data]);

  // Cluster calls by chart index
  const callClusters = useMemo(() => {
    if (!chartData.length || !calls.length) return [];
    const dateSet = new Map<string, number>();
    chartData.forEach((d) => dateSet.set(d.date, d.idx));

    // Group calls by matched chart index
    const groups = new Map<number, InfluencerCall[]>();

    const CHART_ACTIONS = new Set(["BUY", "SHORT", "SELL", "TRIM", "CLOSE", "HOLD"]);
    for (const call of calls.filter((c) => CHART_ACTIONS.has(c.action))) {
      const callDate = call.date.split("T")[0];
      let matchIdx: number | null = null;
      if (dateSet.has(callDate)) {
        matchIdx = dateSet.get(callDate)!;
      } else {
        for (let i = chartData.length - 1; i >= 0; i--) {
          if (chartData[i].date <= callDate) {
            matchIdx = i;
            break;
          }
        }
      }
      if (matchIdx === null) continue;
      const group = groups.get(matchIdx) || [];
      group.push(call);
      groups.set(matchIdx, group);
    }

    return [...groups.entries()].map(([idx, groupCalls]) => {
      // Use the first call's priceAtCall or chart close
      const y = groupCalls[0].priceAtCall ?? chartData[idx].close;
      // Determine dominant action for color
      const actionCounts = new Map<string, number>();
      for (const c of groupCalls) {
        actionCounts.set(c.action, (actionCounts.get(c.action) || 0) + 1);
      }
      let dominantAction: string = groupCalls[0].action;
      let maxCount = 0;
      for (const [action, count] of actionCounts) {
        if (count > maxCount) { dominantAction = action; maxCount = count; }
      }
      return { idx, y, calls: groupCalls, count: groupCalls.length, dominantAction };
    });
  }, [chartData, calls]);

  // Collect unique actions for legend
  const legendActions = useMemo(() => {
    const map: Record<string, string> = {};
    for (const cluster of callClusters) {
      for (const c of cluster.calls) {
        map[c.action] = ACTION_COLORS[c.action];
      }
    }
    return map;
  }, [callClusters]);

  // Determine tick interval for X axis
  const tickInterval = useMemo(() => {
    if (chartData.length <= 10) return 1;
    if (chartData.length <= 30) return 5;
    if (chartData.length <= 90) return 15;
    if (chartData.length <= 180) return 30;
    return Math.floor(chartData.length / 6);
  }, [chartData]);

  if (isError) {
    return (
      <div className="text-center py-6 text-muted-foreground text-xs font-mono">
        Chart unavailable
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2 px-6 py-4">
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <Skeleton key={r} className="h-6 w-10 rounded-full" />
          ))}
        </div>
        <Skeleton className="h-[200px] w-full rounded-lg" />
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="text-center py-6 text-muted-foreground text-xs font-mono">
        No price data available
      </div>
    );
  }

  return (
    <div className="px-6 py-4 space-y-3">
      {/* Range selector */}
      <div className="flex gap-1.5">
        {RANGES.map((r) => {
          const isSelected = range === r;
          const isAutoHint = range === "Auto" && r === resolvedRange;
          return (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold transition-colors ${
                isSelected
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : isAutoHint
                    ? "text-muted-foreground border border-primary/20 bg-primary/5"
                    : "text-muted-foreground hover:text-foreground border border-transparent"
              }`}
            >
              {r}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`chartGradient-${ticker}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(0, 0%, 100%)" stopOpacity={0.15} />
              <stop offset="100%" stopColor="hsl(0, 0%, 100%)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="idx"
            tick={{ fontSize: 10, fill: "hsl(215, 14%, 50%)" }}
            tickLine={false}
            axisLine={false}
            interval={tickInterval}
            tickFormatter={(idx: number) => {
              const point = chartData[idx];
              return point ? formatXLabel(point.date, resolvedRange) : "";
            }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "hsl(215, 14%, 50%)" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `$${v.toFixed(v >= 100 ? 0 : 2)}`}
            domain={["dataMin", "auto"]}
            width={55}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload;
              const cluster = callClusters.find((c) => c.idx === p.idx);
              return (
                <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-xl text-xs font-mono space-y-1.5 max-w-[220px]">
                  <div className="text-muted-foreground">{p.date}</div>
                  <div className="text-foreground font-bold">
                    ${Number(p.close).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  {cluster && (
                    <div className="border-t border-border pt-1.5 space-y-1">
                      {cluster.calls.map((c) => (
                        <div key={c.id} className="flex items-center gap-1.5">
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ backgroundColor: ACTION_COLORS[c.action] }}
                          />
                          <span style={{ color: ACTION_COLORS[c.action] }} className="font-semibold">
                            {c.action}
                          </span>
                          {c.priceAtCall != null && (
                            <span className="text-muted-foreground">
                              @ ${c.priceAtCall.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="close"
            stroke="hsl(0, 0%, 100%)"
            strokeWidth={1.5}
            fill={`url(#chartGradient-${ticker})`}
            dot={false}
            activeDot={false}
          />
          {/* Clustered call markers */}
          {callClusters.map((cluster, i) => (
            <ReferenceDot
              key={`cluster-${cluster.idx}-${i}`}
              x={cluster.idx}
              y={cluster.y}
              r={cluster.count > 1 ? 8 : 5}
              fill={ACTION_COLORS[cluster.dominantAction] || "hsl(215, 14%, 50%)"}
              stroke="hsl(220, 20%, 4%)"
              strokeWidth={2}
              style={{ cursor: "pointer" }}
              onClick={() => onCallClick?.(cluster.calls[0].id)}
              shape={(props: any) => {
                const { cx, cy } = props;
                if (cluster.count === 1) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill={ACTION_COLORS[cluster.dominantAction] || "hsl(215, 14%, 50%)"}
                      stroke="hsl(220, 20%, 4%)"
                      strokeWidth={2}
                    />
                  );
                }
                return (
                  <g>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={9}
                      fill={ACTION_COLORS[cluster.dominantAction] || "hsl(215, 14%, 50%)"}
                      stroke="hsl(220, 20%, 4%)"
                      strokeWidth={2}
                    />
                    <text
                      x={cx}
                      y={cy}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="hsl(220, 20%, 4%)"
                      fontSize={9}
                      fontWeight={700}
                      fontFamily="monospace"
                    >
                      {cluster.count}
                    </text>
                  </g>
                );
              }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      {/* Legend */}
      {callClusters.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {Object.entries(legendActions).map(([action, color]) => (
            <div key={action} className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: color, opacity: action === "NOISE" ? 0.5 : 1 }}
              />
              {action}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
